import React from 'react';
import styles from './ViewModeSwitcher.module.scss';
import { useAppSelector } from '../../../redux/hooks';
import Icon from '../Icon/Icon';
import TimeRangeDropdown, { TimeRange } from '../TimeRangeDropdown/TimeRangeDropdown';

export type ViewMode = 'left' | 'right';

export type ViewModeSwitcherForm = { switch: 'left' | 'right'; range: string | null };

export interface ViewModeSwitcherProps {
  mode: ViewMode;
  onChangeMode: (mode: ViewMode) => void;
  disabled?: boolean;
  className?: string;
  timeRanges?: TimeRange[];
  selectedTimeRangeCode?: string | null;
  onSelectTimeRange?: (code: string) => void;
  onChangeForm?: (form: ViewModeSwitcherForm) => void;
  leftIcon?: string;
  rightIcon?: string;
  showSwitch?: boolean;
}

const ViewModeSwitcher: React.FC<ViewModeSwitcherProps> = ({
  mode,
  onChangeMode,
  disabled = false,
  className,
  timeRanges = [],
  selectedTimeRangeCode = null,
  onSelectTimeRange,
  onChangeForm,
  leftIcon,
  rightIcon,
  showSwitch = true,
}) => {
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const isLight = currentTheme === 'light';
  const isLeftSelected = mode === 'left';

  const defaultLeftIconName = 'chart_icon';
  const defaultRightIconName = 'table_icon';

  const leftIconName = leftIcon ?? defaultLeftIconName;
  const rightIconName = rightIcon ?? defaultRightIconName;

  const baseColor = isLight ? '#707070' : '#AEAEB2';
  const selectedColor = isLight ? '#ffffff' : '#000';

  const emitForm = (which: 'left' | 'right', rangeValue: string | null) => {
    onChangeForm?.({ switch: which, range: rangeValue });
  };

  return (
    <div
      className={`${styles.switcher} ${!isLight ? styles.dark : ''} ${className ?? ''}`}
      aria-label="View mode switcher"
    >
      {showSwitch && (
        <div className={styles.leftGroup} role="radiogroup" aria-label="View mode">
          <div
            aria-checked={isLeftSelected}
            aria-label="Left"
            className={`${styles.item} ${isLeftSelected ? styles.selected : ''}`}
            onClick={() => {
              if (disabled) return;
              onChangeMode('left');
              emitForm('left', selectedTimeRangeCode ?? null);
            }}
          >
            <Icon
              name={leftIconName}
              size={13}
              color={isLeftSelected ? selectedColor : baseColor}
            />
          </div>
          <div
            aria-checked={!isLeftSelected}
            aria-label="Right"
            className={`${styles.item} ${!isLeftSelected ? styles.selected : ''}`}
            onClick={() => {
              if (disabled) return;
              onChangeMode('right');
              emitForm('right', selectedTimeRangeCode ?? null);
            }}
          >
            <Icon
              name={rightIconName}
              size={13}
              color={!isLeftSelected ? selectedColor : baseColor}
            />
          </div>
        </div>
      )}
      <div className={styles.rightGroup}>
        {!!timeRanges?.length && (
          <TimeRangeDropdown
            ranges={timeRanges}
            selectedCode={selectedTimeRangeCode}
            onSelect={(code) => {
              onSelectTimeRange && onSelectTimeRange(code);
              emitForm(mode, code);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ViewModeSwitcher;
